<script setup lang="ts">
import {
  computed,
  ref,
  onMounted,
  onActivated,
  onBeforeUnmount,
  nextTick,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { sectionMap, getSectionById } from '~/services/musicSectionMapConfig';
import { musicService } from '~/services/musicService';
import { readPublicMusicList } from '~/utils/public-music-list.mjs';
import { collectionHue } from './collectionHue';
import CoverArt from './CoverArt.vue';
import { collectionLabel } from './listeningPresentation.mjs';
import {
  rankingSummary,
  rankingPage,
  legacyChartLinks,
  HOME_CHART_ORDER,
  type RankingSummary,
} from './rankings';
import { rankingPath, rankingLabel } from './ranking-contract.mjs';
import DailyChallengeCard from './DailyChallengeCard.vue';
import { DAILY_COMPETITION } from './competition.mjs';
import { artwork } from './artwork';
import type { CompetitionSnapshot } from './competition-types';
const props = defineProps<{
  selected?: string;
  summary?: (RankingSummary & { top_songs?: { image_url?: string }[] })[];
  summaryLocale?: string;
  competition?: CompetitionSnapshot;
  collection?: boolean;
}>();
const { t, te, locale } = useI18n();
const { data } = await useAsyncData(
  () => `ranking-summary-${locale.value}`,
  async () => {
    if (props.summary !== undefined && props.summaryLocale === locale.value)
      return props.summary;
    try {
      return await rankingSummary(locale.value);
    } catch {
      return [];
    }
  },
  { lazy: true },
);
// Home can finish loading after the rail mounts; use its current snapshot.
const summaries = computed(() =>
  props.summary !== undefined && props.summaryLocale === locale.value
    ? props.summary
    : data.value,
);
// Summary may omit the challenge configuration. Read one shared detail for the promotion.
const { data: competitionPreview } = await useAsyncData(
  () => `competition-preview-${locale.value}`,
  async () => {
    if (props.collection) return null;
    if (props.competition) return props.competition;
    if (
      !summaries.value?.some((item) => item.ranking_key === DAILY_COMPETITION)
    )
      return null;
    try {
      return (await rankingPage(DAILY_COMPETITION, 1, 1, locale.value))
        .data as CompetitionSnapshot;
    } catch {
      return null;
    }
  },
  { lazy: true },
);
const { data: previews } = useAsyncData(
  () => `chart-artwork-${locale.value}`,
  async () => {
    if (props.collection) return [];
    try {
      return readPublicMusicList(
        await musicService.getFeaturedMusic(
          {},
          { musicmintInlineError: true, timeout: 6500 },
        ),
      );
    } catch {
      return [];
    }
  },
  { server: false, lazy: true },
);
const chartIndex = (path: string) => {
  const index = HOME_CHART_ORDER.indexOf(path);
  return index < 0 ? HOME_CHART_ORDER.length : index;
};
const chartLabel = (path: string, fallback: string) =>
  te(`t24a.charts.${path}`) ? t(`t24a.charts.${path}`) : fallback;
const hues = [150, 330, 220, 18, 340, 6, 46, 262, 196];
const chartHue = (path: string) =>
  hues[chartIndex(path)] ?? collectionHue(path);
const chartArt = (path: string) =>
  props.summary?.find((item) => rankingPath(item.ranking_key) === path)
    ?.top_songs?.[0]?.image_url ||
  previews.value?.find(
    (item: any) =>
      getSectionById(item.section_id, 'rank', item.selected_option)?.path ===
      path,
  )?.top_songs?.[0]?.image_url;
const designedArt = (path: string, rankingKey = '') => {
  const genre = rankingKey.startsWith('music_genre_') ? rankingKey.slice(12) : '';
  return (
    artwork[`charts/${path}`]?.src ||
    (genre
      ? artwork[`genres/${genre === 'hip-hop' ? 'hip-pop' : genre}`]?.src
      : undefined)
  );
};
const charts = computed(() => {
  if (!summaries.value?.length)
    return sectionMap
      .map((section) => ({
        path: section.path,
        label: chartLabel(
          section.path,
          collectionLabel(section.path, section.title, t, te),
        ),
        art: designedArt(section.path),
        competition: undefined,
      }))
      .sort((a, b) => chartIndex(a.path) - chartIndex(b.path));
  const summary = summaries.value
    .map((item) => {
      const path = rankingPath(item.ranking_key);
      return {
        path,
        label: chartLabel(path, rankingLabel(item, t, te)),
        art: designedArt(path, item.ranking_key),
        competition:
          item.ranking_key === DAILY_COMPETITION
            ? props.competition || competitionPreview.value || undefined
            : undefined,
      };
    })
    .sort((a, b) => chartIndex(a.path) - chartIndex(b.path));
  const legacy = legacyChartLinks(summaries.value).map((section) => ({
    path: section.path,
    label: collectionLabel(section.path, section.title, t, te),
    art: designedArt(section.path),
    competition: undefined,
  }));
  return [...summary, ...legacy];
});
const promotion = computed(() =>
  props.selected === rankingPath(DAILY_COMPETITION)
    ? undefined
    : charts.value.find((chart) => chart.competition?.challenge)?.competition,
);
const railCharts = computed(() => {
  const items = charts.value.filter((chart) => chart.path !== rankingPath(DAILY_COMPETITION));
  // The collection heading already names the current section; keep only other cards.
  return props.collection
    ? items.filter(chart => chart.path !== props.selected)
    : items;
});
const rail = ref<HTMLElement | null>(null);
const positions = useState<Record<string, number>>(
  'explore-chart-scroll',
  () => ({}),
);
let restorePending = true;
let resizeObserver: ResizeObserver | undefined;
const positionKey = computed(() => props.collection ? `${props.selected}-${locale.value}` : locale.value);
const remember = () => {
  if (!restorePending && rail.value?.isConnected)
    positions.value[positionKey.value] = rail.value.scrollLeft;
};
const restore = async () => {
  await nextTick();
  if (!restorePending || !rail.value?.isConnected || !rail.value.clientWidth)
    return;
  rail.value.scrollLeft = positions.value[positionKey.value] || 0;
  restorePending = false;
};
watch(
  rail,
  (element) => {
    resizeObserver?.disconnect();
    restorePending = true;
    if (element && import.meta.client) {
      resizeObserver = new ResizeObserver(restore);
      resizeObserver.observe(element);
    }
  },
  { flush: 'post' },
);
const removePageFinish = useNuxtApp().hook('page:finish', restore);
onMounted(restore);
onActivated(() => {
  restorePending = true;
  void restore();
});
onBeforeUnmount(() => {
  remember();
  resizeObserver?.disconnect();
  removePageFinish();
});
</script>

<template>
  <section class="charts-section" :class="{ 'is-collection': collection }" :aria-labelledby="collection ? undefined : 'charts-heading'" :aria-label="collection ? t('music.musicTrending') : undefined">
    <div v-if="!collection" class="sec-head">
      <h2 id="charts-heading" class="t-h2">
        {{ t('t24a.chartsLabel') }}
        <span class="t-serif">{{ t('t24e.updatedDaily') }}</span>
      </h2>
    </div>
    <DailyChallengeCard v-if="!collection && promotion" :chart="promotion" />
    <nav
      ref="rail"
      class="hscroll chart-cards"
      :aria-label="t('music.musicTrending')"
      @scroll.passive="remember"
    >
      <NuxtLink
        v-for="chart in railCharts"
        :key="chart.path"
        :to="`/music/${chart.path}`"
        class="chart-card"
        :style="{ '--collection-h': chartHue(chart.path) }"
        :aria-label="chart.label"
        :aria-current="selected === chart.path ? 'page' : undefined"
        @click="remember"
      >
        <div class="bg" />
        <div v-if="chartArt(chart.path) || chart.art" class="mini">
          <CoverArt
            :src="chartArt(chart.path) || chart.art"
            alt=""
            :width="128"
            :height="128"
          />
        </div>
        <div class="lbl">
          {{ chart.label }}<small>{{ t('t24e.updatedDaily') }}</small>
        </div>
      </NuxtLink>
    </nav>
  </section>
</template>

<style scoped>
.charts-section {
  min-width: 0;
  padding-block: var(--s-6) var(--s-10);
}
.charts-section .chart-cards {
  --col: 172px;
  scroll-snap-type: x proximity;
  padding-block: var(--s-3);
}
.chart-card {
  position: relative;
  isolation: isolate;
  display: block;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: var(--r-lg);
  outline: 2px solid transparent;
  outline-offset: 3px;
  transition:
    outline-color var(--d-quick),
    transform var(--d-base) var(--ease-out);
}
.chart-card[aria-current] {
  outline-color: var(--text);
}
.chart-card:focus-visible {
  outline-color: var(--focus);
}
.chart-card .bg {
  position: absolute;
  inset: 0;
}
.chart-card .lbl {
  position: absolute;
  z-index: 1;
  inset: 14px 14px auto;
  font: 800 22px/1 var(--font-display);
  letter-spacing: -0.04em;
}
.chart-card .lbl small {
  display: block;
  font: 500 11px/1.2 var(--font-mono);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.85;
  margin-top: var(--s-2);
}
.chart-card .mini {
  position: absolute;
  width: 62%;
  right: -10%;
  bottom: -8%;
  transform: rotate(14deg);
  box-shadow: var(--e-2);
  border-radius: var(--r-sm);
  overflow: hidden;
  backface-visibility: hidden;
}
.chart-card:hover {
  transform: translateY(-3px);
}
@media (min-width: 1024px) {
  .charts-section .chart-cards {
    --col: 196px;
  }
  .charts-section {
    padding-bottom: var(--s-12);
  }
}
@media(max-width:767px) {
  .charts-section { padding-block: var(--s-4) var(--s-6); }
  .charts-section .chart-cards { --col: 132px; }
  .chart-cards .chart-card { aspect-ratio: auto; height: 112px; }
  .chart-card .lbl { font-size: var(--t-body-l); inset: var(--s-3); }
  .is-collection .chart-cards { --col: 172px; }
  .is-collection .chart-card { height: 96px; }
}
.charts-section.is-collection { padding-block: 0; }
@media (prefers-reduced-motion: reduce) {
  .chart-card {
    transition: none;
  }
}
</style>
